import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
/** Aperçus éditeur des blocs parcours membre — parité markup avec le front public. */

import type { BlockStyle } from "@/lib/cms-puck/block-style";
import { useBlockTextCss } from "@/hooks/use-block-style-css";

const labelCls = "text-sm font-medium leading-none text-foreground";
const inputShellCls =
  "flex h-9 w-full items-center rounded-md border border-input py-1 text-sm shadow-sm px-3 text-slate-400 bg-transparent";
const cmsPageBtnCls =
  "inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow";
const cmsPageOutlineBtnCls =
  "inline-flex h-10 w-full items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-semibold shadow-sm";

const tabTriggerBase =
  "inline-flex w-full md:w-fit items-center justify-center whitespace-nowrap border-b px-3 py-1 text-sm font-medium transition-all";
const tabTriggerActive =
  "border-[var(--cms-page-fg,#171717)] border-b-2 text-[var(--cms-page-fg,#171717)]";
const tabTriggerInactive = "border-transparent cms-page-text-muted";

const filterSelectTriggerCls =
  "h-9 w-fit min-w-[8rem] rounded-lg border-none bg-transparent px-3 text-sm text-foreground shadow-none";
const filterSearchWrapCls =
  "relative flex h-9 min-w-64 items-center gap-2 rounded-lg border cms-page-border bg-transparent pl-2 text-foreground";
const filterPanelWrapCls =
  "flex h-9 items-center gap-2 rounded-lg border cms-page-border px-3 text-center text-sm text-foreground";

/**
 * Aperçu STATIQUE du formulaire de connexion (parité LoginForm embedded).
 */
export function MemberLoginPreview({ style }: { style?: BlockStyle }) {
  const cmsNew = useCmsNew();
  const textCss = useBlockTextCss(style);

  return (
    <div className="w-full">
      <div className="w-full flex flex-col gap-5">
        <div className="flex flex-col gap-5">
          <div className="grid w-full items-center gap-1.5">
            <p className={labelCls} style={textCss}>
              {cmsNew("cms.new.preview.memberLogin.email", "Adresse e-mail")}
            </p>
            <div className={inputShellCls} />
          </div>
          <div className="grid w-full items-center gap-1.5">
            <p className={labelCls} style={textCss}>
              {cmsNew("cms.new.preview.memberLogin.password", "Mot de passe")}
            </p>
            <div className={inputShellCls} />
          </div>
          <span className="text-left text-sm font-semibold" style={textCss}>
            {cmsNew("cms.new.preview.memberLogin.forgotPassword", "Mot de passe oublié ?")}
          </span>
          <span className={cmsPageBtnCls}>
            {cmsNew("cms.new.preview.memberLogin.submit", "Se connecter")}
          </span>
          <div className="flex flex-col items-center gap-3">
            <p className="text-center text-sm font-normal cms-page-text-muted" style={textCss}>
              {cmsNew("cms.new.preview.memberLogin.noAccount", "Pas encore de compte ?")}
            </p>
            <span className={cmsPageOutlineBtnCls}>
              {cmsNew("cms.new.preview.memberLogin.createAccount", "Créer un compte")}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center mt-6 gap-1">
          <p className="text-center text-[12px] cms-page-text-muted font-semibold" style={textCss}>
            {cmsNew("cms.new.preview.memberLogin.trouble", "Un problème pour vous connecter ?")}
          </p>
          <span className="text-[12px] font-semibold text-[#008bd4]" style={textCss}>
            {cmsNew("cms.new.preview.memberLogin.contactUs", "Contactez-nous")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Aperçu STATIQUE du formulaire d'inscription (parité SignupForm embedded). */
export function MemberSignupPreview({ style }: { style?: BlockStyle }) {
  const cmsNew = useCmsNew();
  const textCss = useBlockTextCss(style);

  return (
    <div className="w-full">
      <div className="w-full flex flex-col gap-5">
        <div className="grid w-full items-center gap-5">
          {[
            cmsNew("cms.new.auto.preview.signup.nom", "Nom"),
            cmsNew("cms.new.auto.preview.signup.prenom", "Prénom"),
            cmsNew("cms.new.auto.preview.signup.email", "Adresse e-mail"),
            cmsNew("cms.new.auto.preview.signup.password", "Mot de passe"),
          ].map((field) => (
            <div key={field} className="grid w-full items-center gap-1.5">
              <p className={labelCls} style={textCss}>
                {field}
              </p>
              <div className={inputShellCls} />
            </div>
          ))}
          <span className={cmsPageBtnCls}>
            {cmsNew("cms.new.auto.preview.signup.submit", "S'inscrire")}
          </span>
        </div>
        <div className="flex flex-col items-center gap-3">
          <p className="text-center text-sm font-normal cms-page-text-muted" style={textCss}>
            {cmsNew("cms.new.auto.preview.signup.already_account", "Déjà un compte ?")}
          </p>
          <span className={cmsPageOutlineBtnCls}>
            {cmsNew("cms.new.auto.preview.signup.login_link", "Se connecter")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Aperçu STATIQUE des onglets profil (parité ProfileTabs embedded). */
export function MemberProfilePreview({ style }: { style?: BlockStyle }) {
  const cmsNew = useCmsNew();
  const textCss = useBlockTextCss(style);

  return (
    <div className="w-full flex flex-col">
      <div className="w-full px-4 md:px-12 pb-12 flex flex-col gap-5">
        <div
          className="inline-flex h-fit w-full items-center justify-center bg-transparent p-1 text-[var(--cms-page-fg,#171717)] flex md:gap-2 justify-start overflow-x-auto overflow-y-hidden"
          role="tablist"
        >
          {[
            cmsNew("cms.new.auto.preview.profile.tab_account", "Compte"),
            cmsNew("cms.new.auto.preview.profile.tab_password", "Mot de passe"),
            cmsNew("cms.new.auto.preview.profile.tab_subscription", "Abonnement"),
            cmsNew("cms.new.auto.preview.profile.tab_orders", "Commandes"),
          ].map((tab, i) => (
            <span
              key={tab}
              role="tab"
              aria-selected={i === 0}
              className={`${tabTriggerBase} ${i === 0 ? tabTriggerActive : tabTriggerInactive}`}
              style={textCss}
            >
              {tab}
            </span>
          ))}
        </div>
        {/* Formulaire `md:max-w-md` de l'ancienne page. PARITÉ front : ProfileTabs. */}
        <div className="mt-2 flex w-full flex-col gap-5 md:max-w-md">
          {[
            cmsNew("cms.new.preview.memberProfile.fieldName", "Nom"),
            cmsNew("cms.new.preview.memberProfile.fieldEmail", "E-mail"),
            cmsNew("cms.new.preview.memberProfile.fieldPhone", "Téléphone"),
          ].map((field) => (
            <div key={field} className="grid w-full items-center gap-1.5">
              <p className={labelCls} style={textCss}>
                {field}
              </p>
              <div className={inputShellCls} />
            </div>
          ))}
          <span className={cmsPageBtnCls}>
            {cmsNew("cms.new.preview.memberProfile.update", "Mettre à jour")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Aperçu STATIQUE de la grille favoris (parité FavoritesContent embedded). */
export function MemberFavoritesPreview({ style }: { style?: BlockStyle }) {
  const cmsNew = useCmsNew();
  const textCss = useBlockTextCss(style);

  return (
    <div className="w-full mx-auto pb-4 px-3">
      <div className="mb-5 flex w-full items-center text-foreground">
        <nav className="flex" aria-label={cmsNew("cms.new.auto.label.breadcrumb", "Breadcrumb")}>
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <span className="inline-flex items-center text-sm font-medium cms-page-link" style={textCss}>
                <svg
                  className="mr-2.5 h-3 w-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z" />
                </svg>
                Accueil
              </span>
            </li>
            <li className="inline-flex items-center">
              <svg
                className="mx-1 h-3 w-3 cms-page-text-muted"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ml-1 text-sm font-medium cms-page-text-muted md:ml-2" style={textCss}>
                {cmsPhrase(cmsNew, "previewFavorites")}
              </span>
            </li>
          </ol>
        </nav>
      </div>

      <div className="hidden md:block">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-1 rounded-lg cms-page-surface p-0.5">
            <div className={filterSelectTriggerCls}>{cmsNew("cms.new.auto.text.date_de_creation", "Date de création")}</div>
            <div className={filterSelectTriggerCls}>{cmsNew("cms.new.auto.text.type_d_apos_uvre", "Type d&apos;œuvre")}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className={filterSearchWrapCls}>
              <span className="shrink-0 text-foreground">⌕</span>
              <span className="text-sm text-slate-400">{cmsNew("cms.new.auto.text.rechercher", "Rechercher")}</span>
            </div>
            <div className={filterPanelWrapCls}>{cmsPhrase(cmsNew, "previewMoreFilters")}</div>
          </div>
        </div>
      </div>

      <div
        className="grid w-full grid-cols-1 gap-8 xs:mb-10 md:mb-0 md:grid-cols-2 lg:grid-cols-4"
        style={{ gridAutoRows: "min-content" }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-fit">
            <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden bg-slate-100" />
            <div className="pt-2 text-center">
              <p className="mb-1 text-xs cms-page-text-muted" style={textCss}>
                N° 2024-001
              </p>
              <p className="text-sm text-foreground" style={textCss}>
                Titre de l&apos;œuvre
              </p>
              <p className="text-xs cms-page-text-muted" style={textCss}>
                Huile sur toile — 1962
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
